import React, {
  useState,
  useEffect,
  forwardRef,
  useRef,
  useMemo,
  useImperativeHandle,
} from 'react';
import {
  Space,
  Modal,
  Popover,
  Button,
  Tooltip,
  Message,
  ImageViewer,
} from '@bedrock/components';
import { ChevronDown, Upload, Link } from '@bedrock/icons-react';
import { useMemoizedFn } from 'ahooks';
import classNames from 'classnames/bind';
import styles from './index.less';
import {
  SUpload,
  AttachmentDots,
  IMAGE_FILE_RE,
} from '#components/SForm/SFormElement/SUpload';
import TextAreaPopover from '#components/SForm/SFormElement/TextAreaPropover';
import { apiService, defs } from '#services';
import {
  attachmentBlobDownloadUrl,
  outsourceAttachmentDownloadUrl,
} from '#services/api';
import { $sType } from '#utils';
import MuseUploadDrawer from './MuseUploadDrawer';

const cx = classNames.bind(styles);

/** 预览关闭哨兵值：ImageViewer 约定 current = -1 表示不展示 */
const PREVIEW_CLOSED_INDEX = -1;

type ClosingReportProps = {
  quote_key?: string;
  value?: defs.DemandSupplierQuoteSettleReportDto;
  onChange?: (value: defs.DemandSupplierQuoteSettleReportDto) => void;
  view?: boolean;
  muse?: number;
  demandId?: number;
  muse_url?: string;
  indexId?: number;
  standardCode1?: string;
  standardCode2?: string;
  muse_archive_status?: string; // 素材归档状态：ARCHIVED_PENDING_SYNC（归档中）、ARCHIVED_SUCCESS（归档成功）、ARCHIVED_FAILED（归档失败）
  muse_file_stats?: string; // 素材文件统计，格式：共xx个：JPG(xx)、PNG(xx)
};

const ClosingReport = forwardRef((props: ClosingReportProps, ref) => {
  const {
    quote_key,
    muse,
    demandId,
    muse_url,
    indexId,
    standardCode1,
    standardCode2,
    muse_archive_status,
    muse_file_stats,
  } = props;
  const {
    value = { content: '', attachments: [], quote_key, attach_blob_ids: [] },
    onChange,
    view = false,
  } = props;
  const uploadRef = useRef<any>(null);

  const [localFileList, setLocalFileList] = useState<any[]>(
    value?.attachments || []
  );
  const [museDrawerVisible, setMuseDrawerVisible] = useState(false);

  const isMuse = muse === 1;
  const isInner = $sType === 'inner';
  const isMuseArchiving = muse_archive_status === 'ARCHIVED_PENDING_SYNC';

  const isUploading = useMemo(() => {
    return localFileList.some((n) => n.status === 'uploading');
  }, [localFileList]);

  // ── 图片预览（仅图片进入队列；PDF 走新页签；其他格式仅下载）──
  /** 当前结案报告行的全部图片附件 */
  const imageFiles = useMemo(
    () =>
      (value?.attachments || []).filter((f: AttachmentDots) =>
        IMAGE_FILE_RE.test(f?.blob?.filename || f?.name || '')
      ),
    [value?.attachments]
  );
  /** 预览队列；PREVIEW_CLOSED_INDEX 表示未打开 */
  const [previewIndex, setPreviewIndex] = useState(PREVIEW_CLOSED_INDEX);
  const [previewImages, setPreviewImages] = useState<{ id: number | string; url: string }[]>([]);

  /**
   * 点击图片：换取全部图片的预览地址后打开查看器，并定位到被点击的那张。
   * - 单张换取失败仅剔除该张，不影响其余图片（alertError: false 避免 N 张图弹 N 个错误提示）
   * - 定位失败（如附件 id 缺失）直接返回，不退化为首图，避免「点 A 却打开 B」
   * - 左右键切换由 ImageViewer 原生提供
   */
  const handlePreviewImage = useMemoizedFn(async (file: AttachmentDots) => {
    const clickedId = file?.blob?.id || file.id;
    const results = await Promise.all(
      imageFiles.map(async (f) => {
        const showId = f?.blob?.id || f.id;
        try {
          const res = isInner
            ? await attachmentBlobDownloadUrl(
                { id: showId, is_blob: true, download: 'preview' },
                { alertError: false }
              )
            : await outsourceAttachmentDownloadUrl(
                { id: showId, is_blob: true, download: 'preview' },
                { alertError: false }
              );
          return { id: showId, url: res?.data as string };
        } catch (error) {
          return { id: showId, url: '' };
        }
      })
    );
    const validImages = results.filter((n) => !!n.url);
    if (!validImages.length) {
      Message.error('附件预览地址获取失败，请稍后重试');
      return;
    }
    const index = validImages.findIndex((n) => n.id === clickedId);
    if (index < 0) return;
    setPreviewImages(validImages);
    setPreviewIndex(index);
  });

  const validateFields = useMemoizedFn(() => {

    return new Promise((resolve, reject) => {
      if (uploadRef.current) {
        uploadRef.current
          ?.validateFields?.()
          .then(() => {
            if (value.content || value.attachments?.length) {
              resolve(true);
            } else {
              reject('请输入结案报告或上传文件');
            }
          })
          .catch(reject);
      } else {
        resolve(true);
      }
    });
  });

  useImperativeHandle(ref, () => {
    return {
      validateFields,
    };
  });

  const renderMuseEntry = () => {
    // 内包端只读模式：展示跳转按钮
    if (isInner && view) {
      return (
        <div>
        <Tooltip title={isMuseArchiving ? undefined : (muse_url ? undefined : '暂无链接')}>
          <Button
            type="link"
            disabled={isMuseArchiving || !muse_url}
            onClick={() => {
              if (muse_url) {
                window.open(muse_url, '_blank');
              }
            }}
          >
            {isMuseArchiving ? '查看素材资源（归档中）' : '查看素材资源'}
          </Button>
        </Tooltip>
        <div className={cx('muse-file-stats')}>{muse_file_stats}</div>
        </div>
      );
    }

    // 供应商端编辑模式：展示上传链接按钮
    if (!view) {
      return (
        <>
          <Button
            type="link"
            icon={<Upload />}
            onClick={() => setMuseDrawerVisible(true)}
          >
            上传素材资源
          </Button>
          <MuseUploadDrawer
            visible={museDrawerVisible}
            onClose={() => setMuseDrawerVisible(false)}
            demandId={demandId!}
            indexId={indexId!}
            standardCode1={standardCode1!}
            standardCode2={standardCode2!}
          />
        </>
      );
    }

    // 供应商端只读模式：展示查看按钮
    return (
      <>
        <Button
          type="link"
          disabled={isMuseArchiving}
          onClick={() => {
            if (!isMuseArchiving) setMuseDrawerVisible(true);
          }}
        >
          {isMuseArchiving ? '查看 Muse 素材（归档中）' : '查看 Muse 素材'}
        </Button>
        {!isMuseArchiving && (
          <MuseUploadDrawer
            visible={museDrawerVisible}
            onClose={() => setMuseDrawerVisible(false)}
            view
            demandId={demandId!}
            indexId={indexId!}
            standardCode1={standardCode1!}
            standardCode2={standardCode2!}
          />
        )}
      </>
    );
  };

  const renderAttachmentEntry = () => {
    return (
      <Space
        className={cx('upload-container', {
          readonly: view,
        })}
        size={[8, 0]}
      >
        {!view && (
          <SUpload
            className={cx('upload-button')}
            fileList={value.attachments as any}
            onPreviewFile={handlePreviewImage}
            beforeUpload={(file: File) => {
              if (/\.xls$/i.test(file.name)) {
                Message.error('不支持上传 .xls 格式文件，请转换为 .xlsx 格式后重新上传');
                return false;
              }
              return true;
            }}
            onChange={(file) => {
              onChange({
                ...value,
                quote_key,
                attachments: file.dataList,
                attach_blob_ids: file.dataList.map((item: any) => item.blob.id),
              });
              setLocalFileList(file.fileList);
            }}
            ref={uploadRef}
            trigger={
              <Tooltip
                title={'上传附件：单个文件不超过 200M'}
                placement="topRight"
              >
                <Button
                  type="checked-neutral"
                  className={cx('upload-trigger')}
                  icon={<Upload />}
                  loading={isUploading}
                />
              </Tooltip>
            }
            tip={null}
          />
        )}
        {!!value?.attachments?.length && (
          <Popover
            content={
              <div className={cx('upload-popover-content')}>
                <SUpload
                  className={cx('upload-list')}
                  maxFileSize={200 * 1024 * 1024}
                  multiple
                  fileList={value.attachments as any}
                  onPreviewFile={handlePreviewImage}
                  onChange={(file) => {
                    onChange({
                      ...value,
                      attachments: file.dataList,
                      attach_blob_ids: file.dataList.map(
                        (item: any) => item.blob.id
                      ),
                    });
                    setLocalFileList(file.fileList);
                  }}
                  view={view}
                  tip={null}
                />
              </div>
            }
            trigger="click"
            disabled={!value?.attachments?.length}
          >
            <Button
              type="link"
              className={cx('upload-trigger')}
              suffixIcon={<ChevronDown />}
            >
              {view
                ? `${value?.attachments?.length || 0}个附件`
                : `共${value?.attachments?.length || 0}个`}
            </Button>
          </Popover>
        )}
        {!value?.attachments?.length && !!view && (
          <span className={cx('file-empty-tip')}>无附件</span>
        )}
      </Space>
    );
  };

  return (
    <div className={cx('closing-report')}>
      {isMuse ? (
        renderMuseEntry()
      ) : (
        <>
          <div className={cx('text-area-container')}>
            <TextAreaPopover
              view={view}
              value={value.content}
              onChange={(e) => {
                onChange({ ...value, quote_key, content: e?.trim?.() || '' });
              }}
            />
          </div>
          {renderAttachmentEntry()}
        </>
      )}

      {/* 图片预览：仅图片进入队列，左右键切换由 ImageViewer 原生提供 */}
      {previewIndex >= 0 && previewImages.length > 0 && (
        <ImageViewer
          images={previewImages.map((n) => ({ url: n.url }))}
          current={previewIndex}
          onClose={() => {
            setPreviewIndex(PREVIEW_CLOSED_INDEX);
            setPreviewImages([]);
          }}
        />
      )}
    </div>
  );
});

export default ClosingReport;
